import React, { useState, useEffect } from 'react';
import '../styles/ConversationHistory.css';
import LoadingIndicator from './LoadingIndicator';
import {
  getConversationList,
  exportConversations,
} from '../services/conversationService';
import { getMessageList } from '../services/messageService';
import { exportMessages } from '../services/messageService';
import { NotificationInfo } from '../types/NotificationInfo';
import { Download } from 'lucide-react';

/**
 * JST でフォーマットするヘルパー
 * @param date Date オブジェクト
 * @param withTime true なら YYYYMMDD_HHMMSS、false なら YYYYMMDD
 */
const formatJST = (date: Date, withTime: boolean = true): string => {
  const options: Intl.DateTimeFormatOptions = withTime
    ? {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }
    : {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    };
  const parts = new Intl.DateTimeFormat('ja-JP', options).formatToParts(date);
  const map: Record<string, string> = {};
  parts.forEach((p) => {
    if (p.type !== 'literal') map[p.type] = p.value;
  });
  if (withTime) {
    return `${map.year}${map.month}${map.day}_${map.hour}${map.minute}${map.second}`;
  }
  return `${map.year}${map.month}${map.day}`;
};

interface Conversation {
  id: number;
  external_contact?: { display_name?: string };
}

interface Message {
  id: number;
  sender_type: 'external' | 'assistant' | 'system' | 'official';
  sender_name: string;
  content: string;
  created_at: string;
}

interface Props {
  setNotification: (notification: NotificationInfo) => void;
}

const ConversationHistory: React.FC<Props> = ({ setNotification }) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [convLoading, setConvLoading] = useState(false);
  const [checkedIds, setCheckedIds] = useState<number[]>([]);

  const [selectedConvId, setSelectedConvId] = useState<number | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [msgLoading, setMsgLoading] = useState(false);

  // 会話一覧取得
  useEffect(() => {
    (async () => {
      setConvLoading(true);
      try {
        const res = await getConversationList();
        setConversations(res.data);
      } catch (e) {
        console.error(e);
        setNotification({
          message: 'チャット履歴の取得に失敗しました。',
          bgColor: '#dc3545',
          textColor: '#fff',
        });
      } finally {
        setConvLoading(false);
      }
    })();
  }, [setNotification]);

  // メッセージ一覧取得
  useEffect(() => {
    if (selectedConvId == null) return;
    (async () => {
      setMsgLoading(true);
      try {
        const res = await getMessageList(selectedConvId);
        setMessages(res.data);
        window.scrollTo(0, document.body.scrollHeight);
      } catch (e) {
        console.error(e);
        setNotification({
          message: 'メッセージ一覧の取得に失敗しました。',
          bgColor: '#dc3545',
          textColor: '#fff',
        });
      } finally {
        setMsgLoading(false);
      }
    })();
  }, [selectedConvId, setNotification]);

  // 戻る
  const handleBack = () => {
    setSelectedConvId(null);
    setMessages([]);
  };

  // チェックボックス切り替え
  const toggleCheck = (id: number) => {
    setCheckedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // 全選択・全解除
  const selectAll = () => setCheckedIds(conversations.map((c) => c.id));
  const clearAll = () => setCheckedIds([]);

  // 一括ダウンロード（ZIP）
  const handleBulkDownload = async () => {
    if (!checkedIds.length) {
      setNotification({
        message: '少なくとも1件選択してください。',
        bgColor: '#ffc107',
      });
      return;
    }
    try {
      const blob = await exportConversations(checkedIds);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // JST でファイル名
      link.download = `chat_histories_${formatJST(new Date())}.zip`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setNotification({
        message: 'ダウンロードに失敗しました。',
        bgColor: '#dc3545',
      });
    }
  };

  // 単一ダウンロード（CSV）
  const handleSingleDownload = async (conv: Conversation) => {
    try {
      const blob = await exportMessages(conv.id);
      const url = URL.createObjectURL(blob);
      const display = conv.external_contact?.display_name || conv.id;
      // JST でファイル名
      const fileName = `chat_${display}_${conv.id}_${formatJST(new Date(), false)}.csv`;
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setNotification({
        message: 'ダウンロードに失敗しました。',
        bgColor: '#dc3545',
      });
    }
  };

  if (selectedConvId !== null) {
    // チャット詳細画面
    const conv = conversations.find((c) => c.id === selectedConvId)!;
    return (
      <div className="conversation-detail">
        <button className="back-button" onClick={handleBack}>
          ← 戻る
        </button>
        <div className="detail-title">
          <span>会話 {selectedConvId}</span>
          <div
            className="single-download"
            onClick={() => handleSingleDownload(conv)}
          >
            <Download size={20} />
            <span className="download-text">CSVダウンロード</span>
          </div>
        </div>
        {msgLoading ? (
          <LoadingIndicator size={30} isFullPage />
        ) : (
          <ul className="message-list-detail">
            {messages.map((m) => (
              <li
                key={m.id}
                className={
                  m.sender_type === 'external'
                    ? 'message-item-left'
                    : m.sender_type === 'assistant'
                      ? 'message-item-right'
                      : 'message-item-system'
                }
              >
                <div className="bubble">
                  <p>{m.content}</p>
                  <span className="timestamp">
                    {new Date(m.created_at).toLocaleString('ja-JP', {
                      timeZone: 'Asia/Tokyo',
                    })}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  // 会話一覧画面
  return (
    <div className="conversation-section">
      <div className="section-title" style={{ marginTop: '0.83em' }}>
        <span>チャット履歴・統計管理</span>
        <div className="bulk-download" onClick={handleBulkDownload}>
          <Download size={24} />
          <span className="download-text">一括ZIPダウンロード</span>
        </div>
      </div>

      {/* 全選択／全解除ボタン */}
      <div className="select-buttons">
        <button onClick={selectAll}>全て選択</button>
        <button onClick={clearAll} style={{ backgroundColor: '#ccc' }}>
          全て解除
        </button>
      </div>

      {convLoading ? (
        <LoadingIndicator size={40} isFullPage />
      ) : conversations.length > 0 ? (
        <ul className="conversation-list">
          {conversations.map((conv) => (
            <li key={conv.id} className="conversation-item">
              <input
                type="checkbox"
                checked={checkedIds.includes(conv.id)}
                onChange={() => toggleCheck(conv.id)}
                placeholder="ID選択"
              />
              <div
                className="conversation-body"
                onClick={() => setSelectedConvId(conv.id)}
              >
                <span className="meta name">
                  <strong>
                    {conv.external_contact?.display_name || '-'} 様
                  </strong>
                </span>
              </div>
              <div
                className="single-download"
                onClick={() => handleSingleDownload(conv)}
              >
                <Download size={16} />
                <span className="download-text">CSV</span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p>チャット履歴はありません。</p>
      )}
    </div>
  );
};

export default ConversationHistory;
